import React, { useState, useEffect } from "react";

const ColumnDropdown = ({ columns, setColumns, onSave, onClose }) => {
  const defaultCols = [
    { name: "name", visible: true, order: 0 },
    { name: "status", visible: true, order: 1 },
    { name: "tags", visible: true, order: 2 },
    { name: "uploaded_at", visible: true, order: 3 }
  ];

  useEffect(() => {
    if (!columns || columns.length === 0) {
      setColumns(defaultCols);
    }
  }, [columns, setColumns]);

  const activeCols = (columns && columns.length > 0) ? columns : defaultCols;
  
  // Strictly enforce booleans in case the API returned string "true"/"false"
  const visibleCols = [...activeCols].filter(c => c.visible === true || c.visible === "true").sort((a,b) => a.order - b.order);
  const hiddenCols = [...activeCols].filter(c => c.visible === false || c.visible === "false" || !c.visible);

  const [dragHoverIdx, setDragHoverIdx] = useState(null);

  const handleToggle = (colName) => {
    if (colName === "name") return; 
    
    const newCols = activeCols.map(c => 
      c.name === colName ? { ...c, visible: !c.visible } : c
    );
    
    const newVisible = newCols.filter(c => c.visible === true || c.visible === "true").sort((a,b) => a.order - b.order);
    const newHidden = newCols.filter(c => c.visible === false || c.visible === "false" || !c.visible);
    
    newVisible.forEach((c, idx) => c.order = idx);
    newHidden.forEach((c, idx) => c.order = newVisible.length + idx);
    
    const finalCols = [...newVisible, ...newHidden];
    setColumns(finalCols);
    if (onSave) onSave(finalCols); // Hits API instantly
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("dragIndex", index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault(); 
    setDragHoverIdx(index);
  };

  const handleDragLeave = () => {
    setDragHoverIdx(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragHoverIdx(null);
    
    const dragIndexStr = e.dataTransfer.getData("dragIndex");
    if (dragIndexStr === null || dragIndexStr === undefined) return;
    const dragIndex = Number(dragIndexStr);
    
    if (dragIndex === dropIndex) return;

    const newVisible = [...visibleCols];
    const draggedItem = newVisible[dragIndex];
    
    newVisible.splice(dragIndex, 1);
    newVisible.splice(dropIndex, 0, draggedItem);
    
    newVisible.forEach((c, idx) => c.order = idx);
    hiddenCols.forEach((c, idx) => c.order = newVisible.length + idx);
    
    const finalCols = [...newVisible, ...hiddenCols];
    
    setColumns(finalCols); // Updates Table Instantly
    if (onSave) onSave(finalCols); // Hits API exactly ONCE when dropped
  };

  const handleReset = () => {
    setColumns(defaultCols);
    if (onSave) onSave(defaultCols);
  };

  const formatName = (name) => {
    if (name === "name") return "Document";
    if (name === "uploaded_at") return "Added On";
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  return (
    <div 
      className="absolute top-[calc(100%+8px)] left-0 w-[250px] bg-white border border-[#E6E6E6] rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.12)] z-[999] font-outfit cursor-default" 
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-center px-4 py-2 border-b border-[#E6E6E6]">
        <h3 className="font-medium text-[#3F3F46] text-[15px]">Columns</h3>
        <button onClick={onClose} className="text-[#707070] hover:text-[#171717] transition-colors">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>

      <div className="px-4 py-2">
        <div className="flex justify-between items-center mb-3">
          <span className="text-[13px] font-medium text-[#707070]">Visible ({visibleCols.length})</span>
          <button onClick={handleReset} className="text-[13px] font-medium text-[#BB42AE] hover:underline">Reset</button>
        </div>

        <div className="space-y-[2px] mb-4">
          {visibleCols.map((col, idx) => (
            <div 
              key={`vis-${col.name}`}
              draggable={true} 
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, idx)}
              className={`flex items-center justify-between group py-2 px-2 rounded-md transition-colors bg-white ${dragHoverIdx === idx ? 'border-t-2 border-t-[#BB42AE] bg-[#F8F3FF]' : 'hover:bg-[#F8F3FF]'}`}
            >
              <div className="flex items-center gap-3 w-full group/tooltip">
                 <div className="relative flex items-center">
                   <input 
                     type="checkbox" 
                     checked={col.visible} 
                     onChange={() => handleToggle(col.name)}
                     disabled={col.name === "name"}
                     className={`w-4 h-4 rounded border-gray-300 ${col.name === "name" ? 'accent-[#000000E6] cursor-not-allowed grayscale' : 'accent-[#8B55DD] cursor-pointer'}`}
                   />
                   {col.name === "name" && (
                     <div className="absolute right-[calc(100%+14px)] top-1/2 -translate-y-1/2 hidden group-hover/tooltip:block w-max bg-[#4A4A4A] text-white text-[12px] px-3 py-2 rounded shadow-lg z-[60] text-left pointer-events-none">
                       This column is required<br/>and cannot be hidden
                       <div className="absolute -right-[4px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[5px] border-l-[#4A4A4A]"></div>
                     </div>
                   )}
                 </div>
                 <span className={`text-[14px] truncate flex-1 ${col.name === "name" ? 'text-[#000000B3] font-medium' : 'text-[#000000B3]'}`}>
                   {formatName(col.name)}
                 </span>
              </div>
              
              <div className="text-gray-400 cursor-grab active:cursor-grabbing hover:text-[#BB42AE] flex flex-col gap-[3px] py-1 px-1">
                <div className="flex gap-[3px]"><div className="w-[3px] h-[3px] rounded-full bg-current"></div><div className="w-[3px] h-[3px] rounded-full bg-current"></div></div>
                <div className="flex gap-[3px]"><div className="w-[3px] h-[3px] rounded-full bg-current"></div><div className="w-[3px] h-[3px] rounded-full bg-current"></div></div>
                <div className="flex gap-[3px]"><div className="w-[3px] h-[3px] rounded-full bg-current"></div><div className="w-[3px] h-[3px] rounded-full bg-current"></div></div>
              </div>
            </div>
          ))}
        </div>

        {hiddenCols.length > 0 && (
          <>
            <div className="text-[13px] font-medium text-[#707070] mb-1 pt-2 px-2 border-t border-[#E6E6E6]">
              Hidden ({hiddenCols.length})
            </div>
            <div className="space-y-[2px]">
              {hiddenCols.map(col => (
                <div key={`hid-${col.name}`} className="flex items-center gap-3 py-2 px-2 hover:bg-[#F8F3FF] rounded-md transition-colors">
                   <input 
                     type="checkbox" 
                     checked={col.visible === true || col.visible === "true"} 
                     onChange={() => handleToggle(col.name)}
                     className="w-4 h-4 rounded border-gray-300 accent-[#BB42AE] cursor-pointer"
                   />
                   <span className="text-[14px] text-[#707070]">{formatName(col.name)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ColumnDropdown;
